import { createTheme } from "@mui/material/styles";

const theme = createTheme({
    palette: {
        primary: {
            main: "#2E8B57", // Sea Green
            light: "#3CB371",
            dark: "#228B22",
            contrastText: "#ffffff",
        },
        secondary: {
            main: "#DAA520", // Goldenrod
            light: "#FFD700",
            dark: "#B8860B",
            contrastText: "#ffffff",
        },
        background: {
            default: "#ffffff",
            paper: "#F0F8F0",
        },
        text: {
            primary: "#2C3E2D",
            secondary: "#5A6B5A",
        },
        success: {
            main: "#059669",
            light: "#D1FAE5",
        },
        error: {
            main: "#DC2626",
            light: "#FEE2E2",
        },
        info: {
            main: "#0EA5E9",
            light: "#E0F2FE",
        },
        warning: {
            main: "#F59E0B",
            light: "#FEF3C7",
        },
    },
    typography: {
        fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            '"Segoe UI"',
            "Roboto",
            "sans-serif",
        ].join(","),
        h1: {
            fontSize: "3rem",
            fontWeight: 700,
            lineHeight: 1.1,
            color: "#2C3E2D",
            "@media (max-width:768px)": {
                fontSize: "2.5rem",
            },
        },
        h2: {
            fontSize: "2.25rem",
            fontWeight: 600,
            lineHeight: 1.2,
            color: "#2C3E2D",
            "@media (max-width:768px)": {
                fontSize: "2rem",
            },
        },
        h3: {
            fontSize: "1.875rem",
            fontWeight: 600,
            lineHeight: 1.3,
            color: "#2C3E2D",
            "@media (max-width:768px)": {
                fontSize: "1.625rem",
            },
        },
        h4: {
            fontSize: "1.5rem",
            fontWeight: 600,
            lineHeight: 1.4,
            color: "#2C3E2D",
        },
        h5: {
            fontSize: "1.25rem",
            fontWeight: 500,
            lineHeight: 1.4,
            color: "#2C3E2D",
        },
        h6: {
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.4,
            color: "#2C3E2D",
        },
        body1: {
            fontSize: "1rem",
            lineHeight: 1.7,
            color: "#5A6B5A",
        },
        body2: {
            fontSize: "0.875rem",
            lineHeight: 1.6,
            color: "#5A6B5A",
        },
        subtitle1: {
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#2C3E2D",
        },
        subtitle2: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#2C3E2D",
        },
    },
    shape: {
        borderRadius: 12,
    },
    spacing: 8,
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: "12px",
                    padding: "12px 24px",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(46, 139, 87, 0.3)",
                    },
                },
                contained: {
                    boxShadow: "0 4px 12px rgba(46, 139, 87, 0.2)",
                    "&:hover": {
                        boxShadow: "0 8px 25px rgba(46, 139, 87, 0.4)",
                    },
                },
                outlined: {
                    borderWidth: "2px",
                    "&:hover": {
                        borderWidth: "2px",
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "16px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(46, 139, 87, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(46, 139, 87, 0.15)",
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "rgba(240, 248, 240, 0.5)",
                        "&:hover fieldset": {
                            borderColor: "#2E8B57",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#2E8B57",
                            boxShadow: "0 0 0 3px rgba(46, 139, 87, 0.1)",
                        },
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background:
                        "linear-gradient(135deg, #2E8B57 0%, #228B22 100%)",
                    boxShadow: "0 4px 20px rgba(46, 139, 87, 0.3)",
                    borderRadius: "0 0 0 0",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "16px",
                },
                elevation1: {
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                },
                elevation2: {
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
                elevation4: {
                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: "20px",
                    fontWeight: 500,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: "12px",
                    fontWeight: 500,
                },
            },
        },
    },
});

export default theme;
